// @ts-nocheck
import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, Typography } from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';

const BORDER_RADIUS = '50% !important';

/**
 * Get styles for the picker day component
 * @param {object} theme - MUI theme object
 * @param {boolean} isHighlight - Whether the day is highlighted
 * @param {boolean} isFirstDay - Whether the day is the first day in range
 * @param {boolean} isLastDay - Whether the day is the last day in range
 * @returns {object} - Style object for the day
 */
const getPickersDaySx = (theme, isHighlight, isFirstDay, isLastDay) =>
  isHighlight
    ? {
        backgroundColor: theme.palette.primary.contrastText,
        borderRadius: 0,
        margin: 0,
        ...(isFirstDay && {
          borderTopLeftRadius: BORDER_RADIUS,
          borderBottomLeftRadius: BORDER_RADIUS,
        }),
        ...(isLastDay && {
          borderTopRightRadius: BORDER_RADIUS,
          borderBottomRightRadius: BORDER_RADIUS,
        }),
      }
    : {};

/**
 * Custom day component for the date picker
 * @param {object} props - Props for the component
 * @returns {JSX.Element} - PickersDay component
 */
const CustomDay = ({ date, fromDate, toDate, pickersDayProps }) => {
  let highlight = false;
  if (toDate && fromDate) {
    highlight = date.isBetween(fromDate, toDate, 'day', '[]');
  }

  const isFirstDay = date.isSame(fromDate, 'day');
  const isLastDay = date.isSame(toDate, 'day');

  return (
    <PickersDay
      {...pickersDayProps}
      sx={(theme) => getPickersDaySx(theme, highlight, isFirstDay, isLastDay)}
    />
  );
};

CustomDay.propTypes = {
  date: PropTypes.shape({
    isBetween: PropTypes.func,
    isSame: PropTypes.func,
  }).isRequired,
  fromDate: PropTypes.shape({
    format: PropTypes.func,
  }),
  toDate: PropTypes.shape({
    format: PropTypes.func,
  }),
  pickersDayProps: PropTypes.shape({}),
};

CustomDay.defaultProps = {
  fromDate: null,
  toDate: null,
  pickersDayProps: {},
};

/**
 * DateRangePicker component for selecting a date range
 * @param {object} props - Props for the component
 * @returns {React.ReactElement} - returns the date range picker component
 */
const DateRangePicker = ({
  format,
  onChange,
  initialFromDate,
  initialToDate,
  TextFieldProps,
  onError,
  isFutureDatesDisabled,
}) => {
  const [fromDate, setFromDate] = useState(
    initialFromDate ? moment(initialFromDate) : null
  );
  const [toDate, setToDate] = useState(
    initialToDate ? moment(initialToDate) : null
  );

  const [fromDateFocused, setFromDateFocused] = useState(false);
  const [toDateFocused, setToDateFocused] = useState(false);

  const Day = useCallback(
    (date, _selectedDates, pickersDayProps) => (
      <CustomDay
        date={date}
        fromDate={fromDate}
        toDate={toDate}
        pickersDayProps={pickersDayProps}
      />
    ),
    [fromDate, toDate]
  );

  /**
   * @function handleError
   * @param {string} reason - error reason
   * @param {string} value - error value
   * @returns {void} - returns nothing
   */
  const handleError = useCallback(
    (reason, value) => {
      if (onError && typeof onError === 'function') {
        onError(reason, value);
      }
    },
    [onError]
  );

  const handleFromDateChange = useCallback(
    (newValue) => {
      setFromDate(newValue);
      if (toDate || toDate === null) {
        onChange(newValue, toDate);
      }
    },
    [setFromDate, toDate, onChange]
  );

  const handleToDateChange = useCallback(
    (newValue) => {
      setToDate(newValue);
      if (fromDate || fromDate === null) {
        onChange(fromDate, newValue);
      }
    },
    [setToDate, fromDate, onChange]
  );

  const getTextFieldSlotProps = useCallback(
    (value, isFocused, onFocus, onBlur) => (ownerState) => ({
      ...TextFieldProps,
      sx: {
        minWidth: 'auto',
        marginTop: '0px',
        marginBottom: '2px',
        '& .MuiInputLabel-root': {
          color: ownerState.error ? 'other.errorLabel' : 'primary.main',
          top: value || isFocused ? '-3px' : '0px',
          opacity: '1',
          fontSize: '14px',
          transform:
            value || isFocused
              ? 'translate(12px, 4px) scale(0.75)'
              : 'translate(12px, 13px) scale(1)',
          transition: 'all 0.2s ease-in-out',
        },
        '& input.MuiInputBase-input': {
          paddingTop: '15px',
          paddingBottom: '4px',
        },
        '& .MuiFilledInput-root': {
          border: 'none',
        },
        ...TextFieldProps?.sx,
      },
      InputProps: {
        ...TextFieldProps?.InputProps,
      },
      InputLabelProps: {
        shrink: false,
      },
      variant: 'filled',
      onFocus: (e) => {
        onFocus();
        TextFieldProps?.onFocus?.(e);
      },
      onBlur: (e) => {
        onBlur();
        TextFieldProps?.onBlur?.(e);
      },
    }),
    [TextFieldProps]
  );

  const handleClearSelection = useCallback(() => {
    setFromDate(null);
    setToDate(null);
    onChange(null, null);
  }, [onChange]);

  const boxSx = useCallback((theme) => {
    return {
      background: theme.palette?.other?.white,
      ...theme.components?.MuiOutlinedInput?.styleOverrides?.root,
      borderRadius: '6px',
    };
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box display="flex" alignItems="center" sx={boxSx}>
        <DatePicker
          label="From date"
          value={
            fromDate && moment(fromDate).isValid() ? moment(fromDate) : null
          }
          onError={handleError}
          format={format}
          onChange={handleFromDateChange}
          slotProps={{
            textField: getTextFieldSlotProps(
              fromDate,
              fromDateFocused,
              () => setFromDateFocused(true),
              () => setFromDateFocused(false)
            ),
          }}
          renderDay={Day}
          maxDate={isFutureDatesDisabled ? moment() : null}
        />
        <Box mx={1}>
          <Typography> To </Typography>
        </Box>
        <DatePicker
          label="To date"
          minDate={
            fromDate && moment(fromDate).isValid() ? moment(fromDate) : null
          }
          value={toDate && moment(toDate).isValid() ? moment(toDate) : null}
          onError={handleError}
          format={format}
          onChange={handleToDateChange}
          slotProps={{
            textField: getTextFieldSlotProps(
              toDate,
              toDateFocused,
              () => setToDateFocused(true),
              () => setToDateFocused(false)
            ),
          }}
          renderDay={Day}
          maxDate={isFutureDatesDisabled ? moment() : null}
        />
        <Box mr={0.5}>
          {fromDate && toDate ? (
            <IconButton
              title="Clear selection"
              size="small"
              onClick={handleClearSelection}
              aria-label="Close"
            >
              <CloseIcon />
            </IconButton>
          ) : null}
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

DateRangePicker.propTypes = {
  format: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  initialFromDate: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  initialToDate: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  TextFieldProps: PropTypes.shape({
    sx: PropTypes.shape({}),
    label: PropTypes.string,
    InputProps: PropTypes.shape({}),
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
  }),
  onError: PropTypes.func,
  isFutureDatesDisabled: PropTypes.bool,
};

DateRangePicker.defaultProps = {
  format: 'DD/MM/YYYY',
  initialFromDate: null,
  initialToDate: null,
  TextFieldProps: {},
  onError: () => {},
  isFutureDatesDisabled: false,
};

export default DateRangePicker;
